import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import type { PhotoCard } from '@photos/shared';
import { applySelectionClick, toggleGroup } from '../lib/selection';
import { useEscape } from './useEscape';

type Pages = UseInfiniteQueryResult<InfiniteData<{ items: PhotoCard[] }, unknown>, Error>;

/**
 * Selection (click, shift-range, select day, select all) and the lightbox index for a paged photo grid. A different
 * result set (`queryKey`) starts with a clean selection and no lightbox.
 */
export function usePhotoGridState(photos: Pages, queryKey: string) {
  const items = useMemo<PhotoCard[]>(() => photos.data?.pages.flatMap((p) => p.items) ?? [], [photos.data]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClicked = useRef<string | null>(null);
  const [lbIndex, setLbIndex] = useState(-1);

  useEffect(() => {
    setSelected(new Set());
    lastClicked.current = null;
    setLbIndex(-1);
  }, [queryKey]);

  // Drop selected ids that left the results (trashed, moved out of view…).
  useEffect(() => {
    setSelected((s) => {
      if (!s.size) return s;
      const ids = new Set(items.map((p) => p.id));
      const next = new Set([...s].filter((id) => ids.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [items]);

  const clear = useCallback(() => setSelected(new Set()), []);
  useEscape(clear, selected.size > 0 && lbIndex < 0);

  const idsRef = useRef<string[]>([]);
  idsRef.current = items.map((p) => p.id);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const onTileClick = useCallback((e: MouseEvent, id: string, viaCheck: boolean) => {
    const sel = selectedRef.current;
    if (viaCheck || sel.size || e.shiftKey || e.metaKey || e.ctrlKey) {
      setSelected(applySelectionClick(sel, idsRef.current, id, lastClicked.current, e.shiftKey));
      lastClicked.current = id;
      return;
    }
    setLbIndex(idsRef.current.indexOf(id));
  }, []);

  const onSelectDay = useCallback((ids: string[]) => setSelected((s) => toggleGroup(s, ids)), []);

  const loadMore = useCallback(() => {
    if (photos.hasNextPage && !photos.isFetchingNextPage) void photos.fetchNextPage();
  }, [photos]);

  const [selectingAll, setSelectingAll] = useState(false);
  const selectAll = async () => {
    setSelectingAll(true);
    try {
      let all = items;
      let hasNext = !!photos.hasNextPage;
      while (hasNext) {
        const r = await photos.fetchNextPage();
        if (r.isError) break;
        all = r.data?.pages.flatMap((p) => p.items) ?? all;
        hasNext = r.hasNextPage;
      }
      setSelected(new Set(all.map((p) => p.id)));
    } finally {
      setSelectingAll(false);
    }
  };

  const selectedPhotos = useMemo(() => items.filter((p) => selected.has(p.id)), [items, selected]);

  return { items, selected, selectedPhotos, clear, onTileClick, onSelectDay, loadMore, selectAll, selectingAll, lbIndex, setLbIndex };
}
